/**
 * MenuButton — a "More ▾" button that opens a small menu of actions (18 Oct 2026, D-1018-HK). The WAI-ARIA menu button
 * pattern, written here because `@radix-ui/react-dropdown-menu` is not in the shared install and nothing new may be
 * installed in the night builds; it reads and drives like Radix's, so it can be swapped for it later.
 *
 *   - the trigger: `aria-haspopup="menu"`, `aria-expanded`, `aria-controls`; Enter, Space and ArrowDown open it on the
 *     first item, ArrowUp on the last; a click toggles it;
 *   - the menu: `role="menu"`, labelled by the trigger; items are `role="menuitem"` with roving focus — ArrowDown /
 *     ArrowUp move (and wrap), Home / End jump, Escape closes and puts focus back on the trigger, Tab closes, a click
 *     outside closes; choosing an item runs its action and closes;
 *   - an item is a button (`onSelect` gets the trigger, so a sheet it opens can return focus there), a router link or a
 *     plain anchor (a `tel:` link).
 * No meaning by colour: every item is words.
 */
import { useEffect, useId, useRef, useState, type AnchorHTMLAttributes, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { usePress } from '@/lib/press';
import { collectPresses, settled } from '@/lib/press-track';

export type MenuItem =
  | { kind: 'button'; id: string; label: ReactNode; onSelect: (trigger: HTMLButtonElement) => unknown; disabled?: boolean; testid?: string; ariaLabel?: string }
  | { kind: 'link'; id: string; label: ReactNode; to: string; testid?: string; ariaLabel?: string }
  | { kind: 'anchor'; id: string; label: ReactNode; anchor: AnchorHTMLAttributes<HTMLAnchorElement>; testid?: string };

export function MenuButton({ label, ariaLabel, items, className, testid, size = 'sm' }: { label: ReactNode; ariaLabel: string; items: MenuItem[]; className?: string; testid?: string; size?: 'sm' | 'default' }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const itemEls = useRef<(HTMLElement | null)[]>([]);
  // D-1023-BUSY: a choice that calls the server makes the trigger say what is happening (the menu has closed behind it)
  const press = usePress();
  const menuId = useId();
  const triggerId = useId();

  // focus follows `active` while the menu is open (roving tabindex)
  useEffect(() => {
    if (!open) return;
    itemEls.current[active]?.focus();
  }, [open, active]);

  // a click or a focus outside closes it
  useEffect(() => {
    if (!open) return;
    const away = (e: Event) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('focusin', away);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('focusin', away);
    };
  }, [open]);

  const openAt = (i: number) => {
    if (press.busy) return;
    setActive(i);
    setOpen(true);
  };
  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) trigger.current?.focus();
  };
  const onTriggerKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openAt(0);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      openAt(Math.max(0, items.length - 1));
    }
  };
  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const n = items.length;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => (a + 1) % n);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => (a - 1 + n) % n);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(n - 1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    } else if (e.key === 'Tab') {
      setOpen(false);
    } else if (e.key === ' ' && (e.target as HTMLElement).tagName === 'A') {
      // Space activates a link item too, as it does a button item
      e.preventDefault();
      (e.target as HTMLElement).click();
    }
  };
  const choose = (it: MenuItem) => {
    if (it.kind === 'button') {
      if (it.disabled) return;
      // close first, then act: a sheet the action opens takes focus itself and returns it to the trigger when it closes
      setOpen(false);
      if (press.busy || press.flying()) return;
      const { result: r, sent } = collectPresses(() => it.onSelect(trigger.current!));
      if ((r && typeof (r as { then?: unknown }).then === 'function') || sent.length) {
        void press.run(
          async () => {
            if (r && typeof (r as { then?: unknown }).then === 'function') await r;
            await settled(sent);
          },
          { label: typeof it.label === 'string' ? it.label : (it.ariaLabel ?? '') },
        );
      }
      return;
    }
    // a link navigates on its own (the router, or the dialer); the menu closes behind it
    setOpen(false);
  };

  if (!items.length) return null;
  return (
    <div ref={wrap} className={cn('menu-wrap', className)}>
      <button
        ref={trigger}
        id={triggerId}
        type="button"
        className={cn('btn secondary', size === 'sm' ? 'sm' : '', 'menu-trigger')}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={ariaLabel}
        aria-busy={press.busy || undefined}
        onClick={() => (press.busy ? undefined : open ? close() : openAt(0))}
        onKeyDown={onTriggerKey}
        data-testid={testid}
      >
        {press.busy ? (
          <>
            <span className="busy-spin" aria-hidden="true" />
            <span className="busy-words">{press.text}</span>
          </>
        ) : (
          <>
            {label}{' '}
            <span aria-hidden="true" className="menu-caret">
              ▾
            </span>
          </>
        )}
      </button>
      {open ? (
        <div id={menuId} role="menu" aria-labelledby={triggerId} className="menu-pop" onKeyDown={onMenuKey} data-testid={testid ? `${testid}-menu` : undefined}>
          {items.map((it, i) => {
            const shared = {
              role: 'menuitem' as const,
              tabIndex: i === active ? 0 : -1,
              className: 'menu-item',
              onMouseEnter: () => setActive(i),
              ref: (el: HTMLElement | null) => {
                itemEls.current[i] = el;
              },
              'data-testid': it.testid,
            };
            if (it.kind === 'link')
              return (
                <Link key={it.id} {...shared} to={it.to} aria-label={it.ariaLabel} onClick={() => choose(it)}>
                  {it.label}
                </Link>
              );
            if (it.kind === 'anchor')
              return (
                <a key={it.id} {...shared} {...it.anchor} onClick={(e) => { it.anchor.onClick?.(e); choose(it); }}>
                  {it.label}
                </a>
              );
            return (
              <button key={it.id} type="button" {...shared} aria-label={it.ariaLabel} aria-disabled={it.disabled || undefined} onClick={() => choose(it)}>
                {it.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
